import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { interleave, splitToCols, pickCrowdLockCurrentMarket } from '@/utils';

export function useMarketReorder({
	filteredMarkets,
	event,
	reorderMarketPriority,
	loadMarketData,
	filterSlateId,
	filterMatchId,
	isBulkCreatingMarkets,
	isAddingMarket,
	marketStatusFilter,
	filteredMarketGroups,
}: {
	filteredMarkets: any[];
	event: any;
	reorderMarketPriority: any;
	loadMarketData: any;
	filterSlateId: string;
	filterMatchId: string;
	isBulkCreatingMarkets: boolean;
	isAddingMarket: boolean;
	marketStatusFilter: string;
	filteredMarketGroups: string[];
}) {
	const [isReordering, setIsReordering] = useState(false);

	const [optimisticMarkets, setOptimisticMarkets] = useState<any[]>(filteredMarkets);

	useEffect(() => {
		setOptimisticMarkets(filteredMarkets);
	}, [filteredMarkets]);

	const isDndEnabled =
		!filterSlateId &&
		!filterMatchId &&
		!isBulkCreatingMarkets &&
		!isAddingMarket &&
		marketStatusFilter === 'all' &&
		filteredMarketGroups.length === 0;

	const [leftCol, rightCol] = useMemo(() => splitToCols(optimisticMarkets), [optimisticMarkets]);

	const handleDragEnd = useCallback(
		async (result: any) => {
			if (!isDndEnabled) return;
			const { source, destination } = result;
			if (!destination) return;
			if (source.droppableId === destination.droppableId && source.index === destination.index)
				return;

			const left = [...leftCol];
			const right = [...rightCol];
			const sourceCol = source.droppableId === 'left' ? left : right;
			const destCol = destination.droppableId === 'left' ? left : right;

			const [moved] = sourceCol.splice(source.index, 1);
			destCol.splice(destination.index, 0, moved);

			const reorderedVisible = interleave(left, right);

			try {
				setIsReordering(true);
				setOptimisticMarkets(reorderedVisible);

				await reorderMarketPriority({
					eventId: event.id,
					marketId: moved.id,
					orderedMarketIds: reorderedVisible.map((m) => m.id),
				}).unwrap();

				toast({ title: 'Reordered', description: 'Market order updated.' });
			} catch {
				await loadMarketData?.();
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Failed to reorder markets.',
				});
			} finally {
				setIsReordering(false);
			}
		},
		[isDndEnabled, leftCol, rightCol, event?.id, reorderMarketPriority, loadMarketData]
	);

	const crowdLockCurrentMarket = useMemo(() => {
		return pickCrowdLockCurrentMarket(filteredMarkets || [], { isAdmin: true });
	}, [filteredMarkets]);

	return {
		isReordering,
		setIsReordering,
		isDndEnabled,
		leftCol,
		rightCol,
		handleDragEnd,
		crowdLockCurrentMarket,
	};
}
